import type { Node, NodePositionChange, XYPosition } from 'reactflow';

export interface HelperLinesResult {
  horizontal?: number;
  vertical?: number;
  snapPosition: Partial<XYPosition>;
}

/**
 * Smart alignment guides.
 *
 * Given the position change of a single dragged node, find the closest
 * alignment (within `distance` px) to any other node — matching left/center/
 * right edges for the vertical guide, and top/middle/bottom edges for the
 * horizontal guide. Returns the guide-line coordinates to render and a snapped
 * position for the dragged node.
 *
 * Adapted from the React Flow "helper lines" example, extended with
 * center-to-center alignment for a more Figma-like feel.
 */
export function getHelperLines(
  change: NodePositionChange,
  nodes: Node[],
  distance = 5
): HelperLinesResult {
  const defaultResult: HelperLinesResult = {
    horizontal: undefined,
    vertical: undefined,
    snapPosition: { x: undefined, y: undefined },
  };

  const nodeA = nodes.find((n) => n.id === change.id);
  if (!nodeA || !change.position) return defaultResult;

  const aWidth = nodeA.width ?? 0;
  const aHeight = nodeA.height ?? 0;

  const a = {
    left: change.position.x,
    right: change.position.x + aWidth,
    top: change.position.y,
    bottom: change.position.y + aHeight,
    centerX: change.position.x + aWidth / 2,
    centerY: change.position.y + aHeight / 2,
    width: aWidth,
    height: aHeight,
  };

  // Track the smallest distance found on each axis so the closest guide wins.
  let vDist = distance; // for the vertical guide line (controls x)
  let hDist = distance; // for the horizontal guide line (controls y)

  return nodes
    .filter(
      (n) =>
        n.id !== nodeA.id &&
        // Only align against top-level nodes; child (grouped) nodes use
        // parent-relative coordinates that aren't comparable here.
        !(n as unknown as { parentNode?: string }).parentNode &&
        !(n as unknown as { parentId?: string }).parentId
    )
    .reduce<HelperLinesResult>((result, nodeB) => {
      const bWidth = nodeB.width ?? 0;
      const bHeight = nodeB.height ?? 0;
      const b = {
        left: nodeB.position.x,
        right: nodeB.position.x + bWidth,
        top: nodeB.position.y,
        bottom: nodeB.position.y + bHeight,
        centerX: nodeB.position.x + bWidth / 2,
        centerY: nodeB.position.y + bHeight / 2,
      };

      // --- Vertical guides (x alignment) ---
      // left ↔ left
      if (Math.abs(a.left - b.left) < vDist) {
        result.snapPosition.x = b.left;
        result.vertical = b.left;
        vDist = Math.abs(a.left - b.left);
      }
      // right ↔ right
      if (Math.abs(a.right - b.right) < vDist) {
        result.snapPosition.x = b.right - a.width;
        result.vertical = b.right;
        vDist = Math.abs(a.right - b.right);
      }
      // left ↔ right
      if (Math.abs(a.left - b.right) < vDist) {
        result.snapPosition.x = b.right;
        result.vertical = b.right;
        vDist = Math.abs(a.left - b.right);
      }
      // right ↔ left
      if (Math.abs(a.right - b.left) < vDist) {
        result.snapPosition.x = b.left - a.width;
        result.vertical = b.left;
        vDist = Math.abs(a.right - b.left);
      }
      // centerX ↔ centerX
      if (Math.abs(a.centerX - b.centerX) < vDist) {
        result.snapPosition.x = b.centerX - a.width / 2;
        result.vertical = b.centerX;
        vDist = Math.abs(a.centerX - b.centerX);
      }

      // --- Horizontal guides (y alignment) ---
      // top ↔ top
      if (Math.abs(a.top - b.top) < hDist) {
        result.snapPosition.y = b.top;
        result.horizontal = b.top;
        hDist = Math.abs(a.top - b.top);
      }
      // bottom ↔ bottom
      if (Math.abs(a.bottom - b.bottom) < hDist) {
        result.snapPosition.y = b.bottom - a.height;
        result.horizontal = b.bottom;
        hDist = Math.abs(a.bottom - b.bottom);
      }
      // top ↔ bottom
      if (Math.abs(a.top - b.bottom) < hDist) {
        result.snapPosition.y = b.bottom;
        result.horizontal = b.bottom;
        hDist = Math.abs(a.top - b.bottom);
      }
      // bottom ↔ top
      if (Math.abs(a.bottom - b.top) < hDist) {
        result.snapPosition.y = b.top - a.height;
        result.horizontal = b.top;
        hDist = Math.abs(a.bottom - b.top);
      }
      // centerY ↔ centerY
      if (Math.abs(a.centerY - b.centerY) < hDist) {
        result.snapPosition.y = b.centerY - a.height / 2;
        result.horizontal = b.centerY;
        hDist = Math.abs(a.centerY - b.centerY);
      }

      return result;
    }, defaultResult);
}
